import { useCallback, useMemo } from 'react';
import styled from 'styled-components';

import {
  useRfqBuilderActions,
  useRfqBuilderView,
} from '@paradigm/features/src/unified-rfqs/ui';

import AccountSelect from '#/components/account-selection/AccountSelect';
import { cloudBurst, firefly } from '@paradigm/design-system/src/colors';
import { MOBILE_BREAKPOINT } from '#/containers/layout/constants';
import { useTrackAction } from '@paradigm/features/src/analytics';
import useAccountsWithDefault from '@paradigm/features/src/preferences/default-accounts/useAccountsWithDefault';

export default function AccountField() {
  const { account, venue, product } = useRfqBuilderView();
  const { selectAccount } = useRfqBuilderActions();
  const productCode = product.selectedOption.value;
  const cachedAccounts = useAccountsWithDefault(
    venue.selectedOption.value,
    productCode,
  );
  const trackAction = useTrackAction();

  const handleOnChange = useCallback(
    (value: string) => {
      trackAction('update account', { isDefault: false });
      selectAccount(value);
    },
    [selectAccount, trackAction],
  );

  return useMemo(
    () => (
      <Container>
        <Venue>{venue.selectedOption.label}</Venue>
        <AccountSelect
          onChange={handleOnChange}
          value={account.selectedOption}
          venue={venue.selectedOption.value}
          productCode={productCode}
          cachedAccounts={cachedAccounts}
        />
      </Container>
    ),
    [
      account.selectedOption,
      cachedAccounts,
      handleOnChange,
      productCode,
      venue.selectedOption.label,
      venue.selectedOption.value,
    ],
  );
}

const Container = styled.div`
  background: ${firefly};
  border-radius: 5px;
  padding: 1rem 1rem 0rem;

  & #select-account {
    border: none;

    > div {
      background-color: ${cloudBurst};
    }
    .select-control {
      height: 32px;

      > div {
        padding: 0px 5px;
        > div {
          font-size: 0.875rem;

          @media (max-width: ${MOBILE_BREAKPOINT}px) {
            font-size: 1rem;
          }
        }
      }

      @media (max-width: ${MOBILE_BREAKPOINT}px) {
        height: 40px;
      }
    }
  }
`;

const Venue = styled.div`
  padding-bottom: 0.5rem;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    font-size: 1rem;
    padding-bottom: 1rem;
  }
`;
